import * as React from "react";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import logo from "../../Assets/Images/Logo 4.png";
import AccountCircle from "@mui/icons-material/AccountCircle";

// Use either local or production API URL
const APIURL = window.location.hostname === 'localhost' 
    ? "http://localhost:5400/auth" 
    : "https://crmb.apitrading.lk/auth";

function ResponsiveAppBar({ onLogout, userRole, userName }) {
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  

  const pages = userRole === "superadmin"
    ? [
        { name: "Manage Quotations", path: "/carsSuperAdmin/createQuotation" },
      ]
    : userRole === "admin"
    ? [
        { name: "Manage Quotations", path: "/carsAdmin/createQuotation" },
      ]
    : []; // No pages for other roles

  const settings = userRole === "superadmin" ? ["Profile", "Logout"] : ["Logout"];

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${APIURL}/logout`, {
        method: "POST",
        credentials: "include",
      });

      // Clear ALL session-related data
      localStorage.clear();
      sessionStorage.clear();

      // Update state to reflect logout
      onLogout();
      navigate("/"); // Redirect to login page

      // Refresh the page to ensure session is completely cleared
      window.location.reload();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Dynamic background color based on role
  const getAppBarBackground = () => {
    if (userRole === "superadmin") {
      return "linear-gradient(to right, black , #F15A24,rgb(121, 6, 40))"; // Red combination for superadmin
    } else if (userRole === "admin") {
      return "linear-gradient(to right, black, #788, #F15A24)"; // Existing colors for admin
    } else {
      return "linear-gradient(to right, black, #788, #F15A24)"; // Default for other users
    }
  };

  // Fetch user data when the component mounts
  const fetchUserData = async () => {
    try {
      const response = await fetch(`${APIURL}/me`, {
        method: "GET",
        credentials: "include",
      });
  
      if (response.ok) {
        const userData = await response.json();
        console.log("User data:", userData);
        // setUsername(userData.username); // Add your setter if needed
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch user data", response.status, errorText);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };
  

  return (
    <AppBar
      position="static"
      sx={{
        background: getAppBarBackground(), // Dynamic background
        backdropFilter: "blur(50px)",
        boxShadow: "none",
        borderBottom: "2px solid rgba(255, 255, 255, 0.2)",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo */}
          <img
            src={logo}
            alt="Logo"
            style={{ height: "30px", marginRight: "10px", display: { xs: 'none', md: 'block' } }}
          />

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" }, minWidth: 'auto' }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {pages.map((page) => (
                <MenuItem key={page.name} onClick={() => { handleCloseNavMenu(); navigate(page.path); }}>
                  <Typography sx={{ textAlign: "center", color: "#F15A24", "&:hover": { color: "white" } }}>
                    {page.name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {pages.map((page) => (
              <Button
                key={page.name}
                onClick={() => navigate(page.path)}
                sx={{
                  my: 2,
                  color: "white",
                  fontWeight: "bold",
                  transition: "color 0.3s ease-in-out",
                  "&:hover": { color: "#F15A24" },
                }}
              >
                {page.name}
              </Button>
            ))}
          </Box>

          {/* Welcome Message */}
          <Typography
            variant="h6"
            sx={{
              flexGrow: 0,
              mr: 2,
              fontSize: {
                xs: '0.9rem', // Smaller font size on extra-small screens
                md: '1.25rem', // Default font size on medium and larger screens
              },
            }}
          >
            {userRole === "superadmin"
              ? `Welcome, Super Admin: ${userName}`
              : userRole === "admin"
                ? `Welcome, Admin: ${userName}`
                : ""}
          </Typography>

          {/* Profile Menu */}
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <AccountCircle style={{ color: "wheat", width: "40px", height: "40px" }} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting}
                  onClick={() => {
                    if (setting === "Logout") {
                      handleLogout();
                    } else {
                      navigate("/carsSuperAdmin/profile");
                    }
                    handleCloseUserMenu(); // Close the menu after clicking
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>{setting}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;